import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './DashboardUI.module.css';
import notificationStyles from './Notifications.module.css';
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../../services/notificationService';
import Toast from '../../components/Toast';

const SCOPE_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Schedule', value: 'schedule' },
  { label: 'Timesheets', value: 'timesheets' },
  { label: 'System', value: 'system' },
];

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getDateBucket(value) {
  const date = new Date(value);
  const now = new Date();

  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const candidateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayDiff = Math.floor((todayStart - candidateStart) / (1000 * 60 * 60 * 24));

  if (dayDiff === 0) return 'Today';
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff <= 7) return 'Last Week';

  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function getScopeLabel(scope) {
  if (!scope) return 'General';
  if (scope === 'timeoff') return 'Schedule';
  return scope.charAt(0).toUpperCase() + scope.slice(1);
}

function getScopeTone(scope) {
  if (scope === 'schedule' || scope === 'timeoff') return notificationStyles.scopeSchedule;
  if (scope === 'timesheets') return notificationStyles.scopeTimesheets;
  if (scope === 'system' || scope === 'projects') return notificationStyles.scopeSystem;
  return notificationStyles.scopeNeutral;
}

function getScopeIcon(scope) {
  if (scope === 'schedule' || scope === 'timeoff') return '✓';
  if (scope === 'timesheets') return '●';
  if (scope === 'system' || scope === 'projects') return '◆';
  return '•';
}

export default function Notifications() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const selectedScope = useMemo(() => {
    const scope = searchParams.get('scope') || 'all';
    return SCOPE_OPTIONS.some((opt) => opt.value === scope) ? scope : 'all';
  }, [searchParams]);

  const groupedNotifications = useMemo(() => {
    return notifications.reduce((acc, notification) => {
      const section = getDateBucket(notification.created_at);
      if (!acc[section]) acc[section] = [];
      acc[section].push(notification);
      return acc;
    }, {});
  }, [notifications]);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.is_read).length, [notifications]);

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getNotifications({ scope: selectedScope, limit: 100 });
      setNotifications(data);
    } catch (error) {
      setToast({ message: 'Failed to load notifications', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedScope]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleScopeChange = (scope) => {
    setSearchParams({ scope });
  };

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications((prev) => prev.map((n) => (n.notification_id === id ? { ...n, is_read: true } : n)));
    } catch (error) {
      setToast({ message: 'Failed to mark notification as read', type: 'error' });
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead(selectedScope);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setToast({ message: 'All visible notifications marked as read', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to mark all notifications as read', type: 'error' });
    }
  };

  return (
    <div className={styles.scheduleWrapper}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className={notificationStyles.page}>
        <div className={notificationStyles.header}>
          <div className={notificationStyles.headerLeft}>
            <h2 className={notificationStyles.title}>Notifications</h2>
            <p className={notificationStyles.subtitle}>
              Stay updated with your latest project assignments, schedule changes, and administrative requests.
            </p>
          </div>

          <button
            className={notificationStyles.markAllButton}
            onClick={handleMarkAllRead}
            disabled={unreadCount === 0}
          >
            Mark All Read
          </button>
        </div>

        <div className={notificationStyles.scopeTabs}>
            {SCOPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`${notificationStyles.scopeTab} ${selectedScope === opt.value ? notificationStyles.scopeTabActive : ''}`}
                onClick={() => handleScopeChange(opt.value)}
              >
                {opt.label}
              </button>
            ))}
        </div>

        {loading ? (
          <div className={notificationStyles.emptyState}>
            <p>Loading notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className={notificationStyles.emptyState}>
            <h3>No notifications</h3>
            <p>You're all caught up for this scope.</p>
          </div>
        ) : (
          Object.entries(groupedNotifications).map(([sectionName, sectionNotifications]) => (
            <section key={sectionName} className={notificationStyles.section}>
              <h4 className={notificationStyles.sectionTitle}>{sectionName}</h4>

              <div className={notificationStyles.sectionContent}>
                {sectionNotifications.map((notification) => (
                  <article
                    key={notification.notification_id}
                    className={`${notificationStyles.card} ${notification.is_read ? notificationStyles.cardRead : notificationStyles.cardUnread}`}
                  >
                    <div className={`${notificationStyles.iconWrap} ${getScopeTone(notification.scope)}`}>
                      <span>{getScopeIcon(notification.scope)}</span>
                    </div>

                    <div className={notificationStyles.cardBody}>
                      <div className={notificationStyles.cardHeader}>
                        <h5 className={notificationStyles.cardTitle}>{notification.title}</h5>
                        <span className={`${notificationStyles.scopeTag} ${getScopeTone(notification.scope)}`}>
                          {getScopeLabel(notification.scope)}
                        </span>
                      </div>

                      {notification.message ? (
                        <p className={notificationStyles.cardMessage}>{notification.message}</p>
                      ) : null}

                      <div className={notificationStyles.cardMeta}>
                        <span>{formatDate(notification.created_at)}</span>
                        <span>{notification.event_type?.replace(/_/g, ' ') || 'notification'}</span>
                      </div>
                    </div>

                    <div className={notificationStyles.cardActions}>
                      {!notification.is_read ? (
                        <button
                          className={notificationStyles.markReadButton}
                          onClick={() => handleMarkRead(notification.notification_id)}
                        >
                          Mark Read
                        </button>
                      ) : null}

                      {!notification.is_read ? <span className={notificationStyles.unreadDot} aria-hidden="true" /> : null}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
}
